import React, { useState, ChangeEvent, FormEvent } from 'react';
import { motion } from 'framer-motion';
import Auth from '@aws-amplify/auth';
import Button from 'components/Button';
import Input from 'components/Input';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { useNavigate } from 'routeMap';
import { useAuthWrapperContext } from 'toolbox/src/components/AuthWrapper/AuthWrapper';

export function ConfirmSignUp() {
  const location = useLocation();
  const navigate = useNavigate();
  const { username } = queryString.parse(location?.search);
  const decodedUsername = decodeURIComponent(username as string);
  const { refreshAuthState } = useAuthWrapperContext();

  const [form, setForm] = useState({
    username: decodedUsername || '',
    password: '',
    code: '',
    submitting: false,
    error: false,
  });

  const confirmSignUp = async (submitEvent: FormEvent) => {
    submitEvent.preventDefault();

    setForm({ ...form, submitting: true, error: false });
    try {
      console.log('confirming signup for:', form.username);
      await Auth.confirmSignUp(form.username, form.code);
      await Auth.signIn(form.username, form.password);
      toast.success('Sign up complete. You are now logged in');
      refreshAuthState();
      navigate.home();
    } catch (err) {
      setForm({ ...form, error: true });
      console.warn(err);
      if (err.message === 'Incorrect username or password.') {
        toast.error(`Sorry, that verification code or password is not correct`);
      } else if (
        err.message === 'User cannot be confirmed. Current status is CONFIRMED'
      ) {
        toast.info(
          `Your account is already confirmed so you have been signed in. Please take a moment to fill in your profile.`,
        );
        await Auth.signIn(form.username, form.password);
        refreshAuthState();
        navigate.home();
      }
    } finally {
      setForm({ ...form, submitting: false });
    }
  };

  return (
    <motion.form
      initial={{ scale: 0.8 }}
      animate={{ scale: 1 }}
      exit={{ scale: 0.8 }}
      transition={{ duration: 0.2 }}
      className={`w-full max-w-md p-6 bg-white rounded-lg z-10 ${
        form.error ? 'shake' : ''
      }`}
      onSubmit={confirmSignUp}
    >
      <h2 className="mb-5 text-lg font-semibold text-center text-gray-500">
        New Account Email Verification
      </h2>

      {form.username && (
        <p className="mb-3 text-sm text-gray-600">
          Before you can log in for the first time, we need to verify that{' '}
          <b>{form.username}</b> is your email.
        </p>
      )}

      <p className="mb-3 text-sm text-gray-600">
        Please check your Inbox (or Spam folder) for an email with the subject '
        <i>Your verification code</i>'
      </p>

      <Input
        autoFocus
        label="Verification Code"
        autoComplete="one-time-code"
        value={form.code}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setForm({
            ...form,
            code: e.currentTarget.value.replace(/[^\d]/, ''),
          })
        }
        onResetClick={() => setForm({ ...form, code: '' })}
      />

      <Input
        label="Password"
        type="password"
        autoComplete="current-password"
        value={form.password}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setForm({ ...form, password: e.currentTarget.value })
        }
        onResetClick={() => setForm({ ...form, password: '' })}
      />

      <Button
        className="mr-3 btn btn-white"
        onClick={() => navigate.authenticatorRoot()}
      >
        Cancel
      </Button>

      <Button
        type="submit"
        className="btn disabled:opacity-50"
        disabled={form.submitting}
      >
        Complete Registration
      </Button>

      {form.error && <p>{JSON.stringify(form.error)}</p>}
    </motion.form>
  );
}

export default ConfirmSignUp;
